import { motion } from "framer-motion";
import { Flame, Gamepad2, ShoppingBag } from "lucide-react";
import ArcadeNav from "./ArcadeNav";

const lineAnim = {
    hidden: { y: "112%" },
    show: (i) => ({
        y: 0,
        transition: { delay: 0.2 + i * 0.15, duration: 0.75, ease: [0.16, 1, 0.3, 1] },
    }),
};

export default function HomePage({ onNavigate }) {
    return (
        <section
            data-testid="home-page"
            className="relative flex min-h-svh flex-col overflow-hidden bg-[#060608]"
        >
            <div className="noise-layer" />
            <div
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(ellipse at 50% 110%, rgba(255,59,48,0.14), transparent 55%)",
                }}
            />

            <div className="flex w-full items-center justify-between px-5 pt-5 sm:px-10 sm:pt-7">
                <div className="flex items-center gap-3">
                    <img
                        src="/assets/asset2.png"
                        alt="Grill Enforcers badge"
                        className="h-9 w-auto rounded-sm border border-[#2A2A36] object-cover sm:h-11"
                    />
                    <span className="font-jbmono hidden text-[11px] uppercase tracking-[0.3em] text-[#A1A1B2] sm:block">
                        EST. ON THE ICE
                    </span>
                </div>
                <span className="font-jbmono text-[10px] uppercase tracking-[0.2em] text-[#A1A1B2] sm:text-xs">
                    BBQ SPATULAS — <span className="text-[#FF3B30]">$40</span>
                </span>
            </div>

            <div className="flex flex-1 flex-col items-center justify-center px-5 py-8 text-center">
                <motion.div
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="mb-6 flex items-center gap-3 sm:mb-8"
                >
                    <Flame className="h-4 w-4 text-[#FF3B30]" strokeWidth={2.5} />
                    <span className="font-jbmono text-[9px] uppercase tracking-[0.35em] text-[#A1A1B2] sm:text-[11px]">
                        RECYCLED HOCKEY STICK BBQ SPATULAS
                    </span>
                    <Flame className="h-4 w-4 text-[#FF3B30]" strokeWidth={2.5} />
                </motion.div>

                <div className="overflow-hidden">
                    <motion.h1
                        custom={0}
                        variants={lineAnim}
                        initial="hidden"
                        animate="show"
                        className="font-cond text-[17vw] font-extrabold uppercase leading-[0.82] tracking-tight text-[#F2F2F7] sm:text-[11vw]"
                    >
                        GRILL
                    </motion.h1>
                </div>
                <div className="overflow-hidden">
                    <motion.h1
                        custom={1}
                        variants={lineAnim}
                        initial="hidden"
                        animate="show"
                        className="font-cond text-outline-ember text-[17vw] font-extrabold uppercase leading-[0.82] tracking-tight sm:text-[11vw]"
                    >
                        ENFORCERS
                    </motion.h1>
                </div>

                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7, duration: 0.6 }}
                    className="mt-6 max-w-md text-sm leading-relaxed text-[#A1A1B2] sm:mt-8 sm:text-base"
                >
                    Game-used sticks, heavy serrated steel, pro skate-lace loops. Every spatula earns its
                    spot at the grill.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9, duration: 0.6 }}
                    className="mt-8 flex flex-col gap-3 sm:flex-row"
                >
                    <button
                        data-testid="home-play-button"
                        onClick={() => onNavigate("game")}
                        className="flex items-center justify-center gap-2.5 border-2 border-[#2A2A36] bg-transparent px-4 py-3 font-jbmono text-[9px] uppercase tracking-[0.2em] text-[#A1A1B2] transition-[border-color,color,transform] duration-150 hover:-translate-y-0.5 hover:border-[#F2F2F7] hover:text-[#F2F2F7]"
                    >
                        <Gamepad2 className="h-3.5 w-3.5" strokeWidth={2.5} />
                        SIDE GAME: PATTY FLIP
                    </button>
                    <button
                        data-testid="home-shop-button"
                        onClick={() => onNavigate("shop")}
                        className="flex items-center justify-center gap-2.5 border-2 border-[#FF3B30] bg-[#FF3B30] px-6 py-3 font-jbmono text-[10px] uppercase tracking-[0.2em] text-[#0A0A0C] transition-[transform,background-color,color] duration-150 hover:-translate-y-0.5 hover:bg-transparent hover:text-[#FF3B30]"
                    >
                        <ShoppingBag className="h-4 w-4" strokeWidth={2.5} />
                        SEE THE SPATULAS
                    </button>
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.05, duration: 0.6 }}
                className="px-4 pb-5 sm:pb-6"
            >
                <ArcadeNav active="home" onNavigate={onNavigate} prefix="home" />
            </motion.div>
        </section>
    );
}
